import { DateTime } from 'luxon';
import { GoalKind, ObjectiveEntry, ObjectiveTarget } from 'services/objectives/types';

interface TargetComparison {
  distance: number | null;
  expected: number;
  id: string;
  name: string;
  relation: 'ahead' | 'behind' | 'distance';
}

const formatTrackNumber = (value: number) => {
  const rounded = Math.round(value * 100) / 100;
  return Object.is(rounded, -0) ? '0' : String(rounded);
};

const expectedAt = (
  points: ObjectiveTarget['points'],
  time: number,
) => {
  const sorted = points
    .map(point => ({
      time: DateTime.fromISO(point.at).toMillis(),
      value: point.value,
    }))
    .filter(point => Number.isFinite(point.time) && Number.isFinite(point.value))
    .sort((a, b) => a.time - b.time);
  if (sorted.length === 0 || time < sorted[0].time) {
    return null;
  }
  const last = sorted[sorted.length - 1];
  if (time >= last.time) {
    return last.value;
  }
  const nextIndex = sorted.findIndex(point => point.time >= time);
  if (nextIndex <= 0) {
    return sorted[0].value;
  }
  const right = sorted[nextIndex];
  const left = sorted[nextIndex - 1];
  const span = right.time - left.time;
  if (span === 0) {
    return left.value;
  }
  const ratio = (time - left.time) / span;
  return left.value + ((right.value - left.value) * ratio);
};

const latestEntry = (entries: ObjectiveEntry[]) => {
  const dated = entries
    .map(entry => ({
      entry,
      time: DateTime.fromISO(entry.recordedAt).toMillis(),
    }))
    .filter(item => Number.isFinite(item.time) && Number.isFinite(item.entry.value))
    .sort((a, b) => b.time - a.time || b.entry.id.localeCompare(a.entry.id));
  return dated[0] ?? null;
};

const compareTargets = ({
  entries,
  goalKind,
  targets,
}: {
  entries: ObjectiveEntry[];
  goalKind: GoalKind;
  targets?: ObjectiveTarget[] | null;
}): TargetComparison[] => {
  const latest = latestEntry(entries);
  if (!latest) {
    return [];
  }
  return (targets ?? []).flatMap((target): TargetComparison[] => {
    const expected = expectedAt(target.points, latest.time);
    if (expected == null) {
      return [];
    }
    if (goalKind === 'AROUND') {
      return [{
        distance: Math.abs(latest.entry.value - expected),
        expected,
        id: target.id,
        name: target.name,
        relation: 'distance' as const,
      }];
    }
    const ahead = goalKind === 'LOWEST'
      ? latest.entry.value <= expected
      : latest.entry.value >= expected;
    return [{
      distance: null,
      expected,
      id: target.id,
      name: target.name,
      relation: ahead ? 'ahead' as const : 'behind' as const,
    }];
  });
};

export type { TargetComparison };
export { compareTargets, formatTrackNumber };
